import { useMemo } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { Announcement } from '../components/common/AnnouncementBanner';

/**
 * Hook to generate announcements based on subscription status and other sources.
 * 
 * This hook is extensible - you can add more announcement sources here:
 * - Subscription expiration warnings
 * - Promotional announcements (from API in future)
 * - System maintenance notices
 * - Feature announcements
 */
export const useAnnouncements = (): Announcement[] => {
  const { status, daysRemaining, plan, isLoading } = useSubscription();
  const { decodedToken } = useAuth();

  const announcements = useMemo(() => {
    const result: Announcement[] = [];

    // Only show announcements to school admins (not system admins or other users)
    if (isLoading || !decodedToken?.isSchoolAdmin) {
      return result;
    }

    // =========================================================================
    // SUBSCRIPTION EXPIRATION WARNINGS
    // =========================================================================
    
    const isTrial = status === 'trial';
    const isActive = status === 'active';
    const shouldWarn = (isTrial || isActive) && daysRemaining <= 7 && daysRemaining > 0;

    if (shouldWarn) {
      const planName = plan?.name || (isTrial ? 'Trial' : 'subscription');
      const daysText = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
      
      // Determine urgency based on days remaining
      let type: 'warning' | 'error' = 'warning';
      let message = '';

      if (daysRemaining <= 2) {
        type = 'error';
        message = isTrial
          ? `Your trial ends in ${daysText}! Contact support now to continue using Edubook.`
          : `Your ${planName} plan expires in ${daysText}! Renew now to avoid interruption.`;
      } else if (daysRemaining <= 5) {
        type = 'warning';
        message = isTrial
          ? `Your trial expires in ${daysText}. Contact support to upgrade and keep your data.`
          : `Your ${planName} plan expires in ${daysText}. Please contact support to renew.`;
      } else {
        type = 'warning';
        message = isTrial
          ? `Your trial period ends in ${daysText}. Contact support to continue using all features.`
          : `Your ${planName} plan expires in ${daysText}. Contact support to extend your subscription.`;
      }

      result.push({
        id: `subscription-expiry-${status}`,
        type,
        message,
        actionLabel: 'Contact Support',
        actionUrl: 'mailto:support@edubook.com?subject=Subscription%20Renewal',
        dismissible: true,
      });
    }

    // =========================================================================
    // PENDING PAYMENT WARNING
    // =========================================================================
    
    if (status === 'pending_payment') {
      result.push({
        id: 'pending-payment',
        type: 'warning',
        message: 'Your payment is pending. Please complete the payment to continue using all features.',
        actionLabel: 'Contact Support',
        actionUrl: 'mailto:support@edubook.com?subject=Payment%20Assistance',
        dismissible: true,
      });
    }

    // =========================================================================
    // FUTURE: Add more announcement sources here
    // =========================================================================
    
    // Example: Promotional announcements (can be fetched from API)
    // if (promoAnnouncements) {
    //   result.push(...promoAnnouncements);
    // }

    // Example: System maintenance notices
    // if (maintenanceNotice) {
    //   result.push({
    //     id: 'maintenance',
    //     type: 'info',
    //     message: 'Scheduled maintenance on Sunday 2 AM - 4 AM IST.',
    //     dismissible: true,
    //   });
    // }

    return result;
  }, [status, daysRemaining, plan, decodedToken?.isSchoolAdmin, isLoading]);

  return announcements;
};

export default useAnnouncements;
